import { RouteFactory, routesVerticals } from "@app/routePaths";
import {
  Author,
  ContributorProfile,
  Element,
  IframeResizeOptions,
  Media,
  Others,
  Section,
  Storyline,
  StoryThread,
  Tag,
} from "@app/types/Cue";
import { AccessFlag, KickerEnums } from "@app/types/enums";
import {
  AdSettingsProps,
  AdUnitNames,
} from "@components/Advertisement/utils/types";
import { getAuthorProfile } from "@components/Byline/BylineHelper";
import { GADataProps } from "@components/GAData/GAData";
import {
  getArticleAuthorNamesV2,
  getArticleKeywords,
  getArticleStoryThread,
} from "@components/GAData/helper";
import { renderPageTitleV2 } from "@components/MetaTags/helpers";
import { MetaTagDescriptor, MetaTags } from "@components/MetaTags/MetaTags";
import { replaceProblematicSymbols } from "@routes/Article/components/ArticleParagraphElements/utils/helpers";
import { ArticlePaidModeType } from "@routes/Article/utils/enums";
import {
  FeedArticleItems,
  FeedAuthor,
  ShortenerUrlModel,
} from "@routes/Article/utils/types";
import {
  GLOBAL_KEYWORD,
  GLOBAL_SECTIONS,
} from "@routes/Global/utils/constants";
import { FeedTag } from "@routes/Keyword/utils/types";
import AseanBanner from "@routes/Section/layouts/Verticals/AseanBusiness/components/AseanBanner";
import GlobalEnterpriseBanner from "@routes/Section/layouts/Verticals/GlobalEnterprise/components/GlobalEnterpriseBanner";
import SMEBanner from "@routes/Section/layouts/Verticals/SME/components/SMEBanner";
import { TagType } from "@routes/Section/types";
import { getFormattedTextForAds } from "@routes/Section/utils/helpers";
import { FeedSection } from "@routes/Section/utils/types";
import {
  CDP_PAYWALL_30_DAYS_MODULE_ID,
  CDP_PAYWALL_180_DAYS_MODULE_ID,
  CDP_PAYWALL_MODULE_ID,
  dayjsSingaporeTimezone,
  getEnvironment,
} from "@util/constant";
import {
  getArticleJSONLD,
  getEnvironmentDomain,
  getSectionUrlPath,
  textToSlug,
} from "@util/helpers";
import dayjs from "dayjs";
import { isEmpty } from "lodash-es";

import {
  ELEMENTS_TYPE_TO_EXCLUDE,
  KEYWORDS_TO_DISPLAY_MORE_SECTION,
} from "./constants";

export const VerticalHeaderFactory = ({
  verticalPath,
}: {
  verticalPath: string;
}) => {
  switch (verticalPath) {
    case RouteFactory.globalEnterprise:
      return GlobalEnterpriseBanner;

    case RouteFactory.aseanBusiness:
    case RouteFactory.newslettersAseanBusiness:
      return AseanBanner;

    case RouteFactory.sgsme:
    case RouteFactory.newslettersSgsme:
      return SMEBanner;
  }
};

/**
 * Checks if paywall should be displayed on Articles
 * Examples of articles shown on Article.test.tsx
 * @param {Object} props
 * @param {boolean} props.isGiftReceived If article was received as a gift
 * @param {string} props.variant "default" || "full"
 * @param {string} props.contentAccess "0" || "1"
 * @returns {boolean}
 */
export const checkDisplayPaywall = ({
  isGiftReceived,
  variant,
  contentAccess,
}: {
  isGiftReceived: boolean;
  variant: string | undefined;
  contentAccess: string;
}): boolean => {
  if (isGiftReceived || variant === "full") return false;

  return contentAccess === "1";
};

/**
 * Transpose the article elements and retrieve the annotations and elements values.
 * @param storyLine The storyline of the article.
 * @param elements The full list of elements in the article.
 * @param isSubscriber The user type.
 * @param isPremium The article content access.
 * @returns {Element[]}
 */
export const getResolvedArticleElements = (
  storyLine: Storyline = [],
  elements: Element[],
  isTruncated: boolean = true
): Element[] => {
  // Get the list of elements based on storyline
  const elementsWithAnnotationRef = getArticleElementsWithAnnotationReference(
    storyLine,
    elements
  );

  // Remove elements that are not needed
  const elementsWithoutExcludedType = elementsWithAnnotationRef.filter(
    ({ type }) => !ELEMENTS_TYPE_TO_EXCLUDE.includes(type.toString())
  );

  // Add reference to elements that has children
  const elementsWithListRef = elementsWithoutExcludedType.map((element) => {
    const reference = element.children
      ?.map((child) => elements.find((element) => child === element.id))
      .filter((element): element is Element => !!element);

    if (typeof reference === "undefined" || isEmpty(reference)) return element;

    const annotationWithRef = getAnnotationReference(reference, elements);

    return { ...element, reference: annotationWithRef };
  });

  // Return first three elements.
  if (isTruncated) return elementsWithListRef.slice(0, 3);

  // Return full list of elements.
  return elementsWithListRef;
};

/**
 * Retrieves the annotation reference for each element in the given array of elements.
 *
 * @param elements - The array of elements to retrieve the annotation reference for.
 * @param elementSource - The array of element sources to search for the annotation reference.
 * @returns An array of elements with their corresponding annotation references.
 */
const getAnnotationReference = (
  elements: Element[],
  elementSource: Element[]
): Element[] => {
  const annotationWithRef = elements.map((element) => {
    const fields = element.fields.map((field) => {
      const annotations = field?.annotations?.map((annotation) => {
        const element = elementSource.find((element) => {
          return element.id === annotation.value;
        });

        if (element) {
          return {
            ...annotation,
            reference: element,
          };
        }

        return annotation;
      });

      if (typeof annotations === "undefined" || isEmpty(annotations))
        return field;

      return { ...field, annotations };
    });

    return { ...element, fields };
  });

  return annotationWithRef;
};

/**
 * Retrieves the list of article elements with annotation references based on the storyline and elements.
 * @param {Storyline} storyLine The storyline of the article.
 * @param {Element[]} elements The list of all elements in the article.
 * @returns {Element[]} The list of article elements with annotation references.
 */
export const getArticleElementsWithAnnotationReference = (
  storyLine: Storyline = [],
  elements: Element[]
): Element[] => {
  const articleElements: Element[] = storyLine
    .map((id) => elements.find((element) => element.id === id)) // Get the list of elements based on storyline
    .filter((element): element is Element => !!element); // Remove undefined elements

  const articleElementsWithAnnotationsRef = getAnnotationReference(
    articleElements,
    elements
  );

  return articleElementsWithAnnotationsRef;
};

/**
 * Check if it a branded content
 *
 * @param kicker
 */
export const getIsBrandedContent = (kicker: string) => {
  return (
    kicker.toUpperCase().replace(/\s/g, "") === KickerEnums.BRANDED_CONTENT
  );
};

/**
 * Check if the array of keywords contains the keyword to query more article based on keywords
 *
 * @param Tag
 */
export const getKeywordsToDisplayItems = (
  keywords: FeedTag[]
): FeedTag | undefined => {
  const key = keywords.find((keyword) => {
    const urlPath = getUrlPathFromAbsoluteUrl(keyword.urlPath);

    return KEYWORDS_TO_DISPLAY_MORE_SECTION.includes(urlPath) ? keyword : null;
  });

  return key;
};

/**
 * Get the dependent paths of the sections
 *
 * @param sections
 * @returns {string[]}
 */
export const getSectionDependentPaths = (sections?: Section[]): string[] => {
  if (!sections) return [];

  const paths = sections.reduce<string[]>((previousValue, currentValue) => {
    const path = `/${currentValue.uniqueName.replace("_", "/")}`;

    if (routesVerticals.includes(path)) {
      return [...previousValue, path, `${path}/latest`];
    }

    // Newsletters paths for morning and evening newsletters
    const dailyNewsletters = [
      RouteFactory.newslettersBreakfastBrief,
      RouteFactory.newslettersDailyDebrief,
    ];

    // Add newsletter daily path to the list
    if (dailyNewsletters.includes(path)) {
      return [...previousValue, path, RouteFactory.newslettersDaily];
    }

    return [...previousValue, path];
  }, []);

  return [...new Set(paths)];
};

/*
 * Get the dependent paths of the keywords
 *
 * @param tags
 * @returns {string[]}
 */
export const getKeywordsDependentPaths = (keywords: Tag[]): string[] => {
  if (!keywords) return [];

  const paths = keywords.map((keyword) => keyword.urlPath);

  return paths;
};

/**
 * Helper function to get the dependent paths of articles from its author(s).
 *
 * @param authors List of authors from article.
 * @returns {string[]}
 */
export const getPathsFromAuthors = (authors: Author[] = []): string[] => {
  const paths = authors
    .map((author) => {
      const { currentAuthorProfile } = getAuthorProfile(author.profiles);

      return currentAuthorProfile?.content.urlPath;
    })
    .filter((path): path is string => {
      return !!path;
    });

  return paths;
};

/**
 * Helper function to get the dependent paths of articles from its contributor profile(s).
 *
 * @param contributorProfiles List of contributor profiles.
 * @returns {string[]}
 */
export const getPathsFromContributors = (
  contributorProfiles: ContributorProfile[]
): string[] => {
  const paths = contributorProfiles
    .map((profile) => {
      return profile.content.urlPath;
    })
    .filter((path): path is string => {
      return !!path;
    });

  return paths;
};

/**
 * Helper function to get the dependent paths of articles from story threads.
 *
 * @param others The others object from the article.
 * @returns {string[]}
 */
export const getPathsFromStoryThreads = (others?: Others): string[] => {
  const storyThread = others?.storyThread;

  if (typeof storyThread == "undefined") return [];

  try {
    const storyThreadData: StoryThread[] = JSON.parse(storyThread);
    const storyThreadObject = storyThreadData.at(0);

    if (typeof storyThreadObject === "undefined") return [];

    const path = textToSlug(storyThreadObject.value);

    return [`/${path}`];
  } catch (error) {
    return [];
  }
};

/**
 * Determines the supplement-dependent paths based on article tags
 * @param {Tag[]} tags - Array of article tags to analyze
 * @returns {string[]} Array of supplement paths. Returns empty array if no supplement matches
 */
export const getSupplementsDependenthPaths = (
  tags: Tag[] | FeedTag[]
): string[] => {
  if (isWealthSupplement(tags)) return [RouteFactory.wealthSupplements];
  if (isPropertySupplement(tags)) return [RouteFactory.propertySupplements];

  return [];
};

/**
 * Get the dependent paths for the global context based on tags and section.
 *
 * @param tags - Array of tags associated with the article.
 * @param section - Array of sections associated with the article.
 * @returns {string[]}
 */
export const getGlobalDependentPaths = (
  tags: FeedTag[],
  section: FeedSection[]
): string[] => {
  const hasGlobalKeyword = tags.some(
    ({ urlPath }) => getUrlPathFromAbsoluteUrl(urlPath) === GLOBAL_KEYWORD
  );
  const hasGlobalSection = section.some(({ uniqueName }) =>
    GLOBAL_SECTIONS.includes(uniqueName)
  );

  if (hasGlobalKeyword && hasGlobalSection) {
    return [RouteFactory.global];
  }

  return [];
};

/**
 * Get the dependent paths for the pulse based on if summary is avaliable.
 *
 * @param id - Article ID.
 * @param isSummaryAvailable - Boolean indicating if summary is available.
 * @returns {string[]}
 */
export const getPulseDependentPath = (
  id: string,
  isSummaryAvailable: boolean
): string[] => {
  if (isSummaryAvailable && id) return [RouteFactory.pulseSlide(id)];

  return [];
};

/**
 * Determines if the article has Wealth story thread based on its tags
 * @param {Tag[]} tags - Array of article tags to analyze
 * @returns {boolean} True if article is part of Wealth supplement, false otherwise
 */
export const isWealthSupplement = (tags: Tag[] | FeedTag[]): boolean => {
  return tags.some(({ type, sections }) => {
    if (type !== TagType.StoryThread) return false;
    if (!sections || sections.length === 0) return false;

    return sections.some(({ uniqueName }) => uniqueName === "wealth");
  });
};

/**
 * Determines if the article has Property story thread based on its tags
 * @param {Tag[]} tags - Array of article tags to analyze
 * @returns {boolean} True if article is part of Property supplement, false otherwise
 */
export const isPropertySupplement = (tags: Tag[] | FeedTag[]): boolean => {
  return tags.some(({ type, sections }) => {
    if (type !== TagType.StoryThread) return false;
    if (!sections || sections.length === 0) return false;

    return sections.some(({ uniqueName }) => uniqueName === "property");
  });
};

// Get iframe attributes without the onload handler
export const getIframeAttributes = (
  id: string,
  cssClass: string = ""
): Record<string, string> => {
  return {
    id,
    className: `iframe-auto-resize${cssClass ? ` ${cssClass}` : ""}`,
    width: "100%",
    height: "auto",
    style: "min-height: 400px;",
  };
};

// Generate the resize script with options
export const generateResizeScript = (
  id: string,
  options: IframeResizeOptions = {}
): string => {
  const defaultOptions = {
    log: false,
    heightCalculationMethod: "lowestElement",
    onResized:
      "function(messageData) { ContentHeight.postMessage(messageData.height); }",
  };

  const finalOptions = { ...defaultOptions, ...options };

  return `
    <script type="application/javascript">
      iFrameResize({
        log: ${finalOptions.log},
        heightCalculationMethod: '${finalOptions.heightCalculationMethod}',
        onResized: ${finalOptions.onResized}
      }, '#${id}');
    </script>
  `;
};

/**
 * Helper function to get the article main section path.
 * @param section The section object.
 * @returns {string | null}
 */
export const getArticleMainSectionPath = (section?: Section): string | null => {
  if (typeof section === "undefined") return null;

  return getSectionUrlPath(section);
};

/**
 * Retrieves the URL of the "original-caas" image from the first element in the `ogImages` array.
 *
 * @param {Media[]} ogImages - An array of Media objects containing image metadata.
 * @returns {string | null} The URL of the "original-caas" image if available, otherwise `undefined`.
 */
export function getOgImageUrl(
  ogImages: Media[] | undefined
): string | undefined {
  if (!ogImages || ogImages.length === 0) return undefined;

  return ogImages[0]?.content?.fields?.["original-caas"]?.url;
}

/** The following function is designed to be compatible with the new backend. - AUG 2025 */
export const getDependentPathsBySection = (
  sections?: FeedSection[]
): string[] => {
  if (!sections) return [];

  const paths = sections.reduce<string[]>((previousValue, currentValue) => {
    const path = getUrlPathFromAbsoluteUrl(currentValue.urlPath);

    if (routesVerticals.includes(path)) {
      return [...previousValue, path, `${path}/latest`];
    }

    // Newsletters paths for morning and evening newsletters
    const dailyNewsletters = [
      RouteFactory.newslettersBreakfastBrief,
      RouteFactory.newslettersDailyDebrief,
    ];

    // Add newsletter daily path to the list
    if (dailyNewsletters.includes(path)) {
      return [...previousValue, path, RouteFactory.newslettersDaily];
    }

    return [...previousValue, path];
  }, []);

  return [...new Set(paths)];
};

/*
 * Get the dependent paths of the keywords and story threads
 *
 */
export const getDependentPathsByTags = (keywords: FeedTag[]): string[] => {
  if (!keywords) return [];

  const paths = keywords.map((keyword) =>
    getUrlPathFromAbsoluteUrl(keyword.urlPath)
  );

  return [...new Set(paths)];
};

/**
 * Helper function to get the dependent paths of articles from its author(s).
 *
 * @param authors List of authors from article.
 * @returns {string[]}
 */
export const getDependentPathsByAuthors = (
  authors: FeedAuthor[] = []
): string[] => {
  const paths = authors
    .map((author) => {
      return getUrlPathFromAbsoluteUrl(author.urlPath);
    })
    .filter((path): path is string => {
      return !!path;
    });

  return paths;
};

/**
 * Extracts the first paragraph text from an array of HTML strings.
 * It iterates through the HTML strings, creates a temporary DOM element to parse the HTML,
 * and retrieves the text content of the first `<p>` element found.
 * If no `<p>` element is found, it returns `null`.
 * @param htmlArray An array of HTML strings.
 * @returns The text content of the first `<p>` element or `null` if not found.
 */
export const getFirstParagraphText = (
  htmlArray: string[]
): string | undefined => {
  if (!htmlArray || htmlArray.length === 0) return "";
  if (typeof window === "undefined") return "";

  for (const html of htmlArray) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;

    const firstP = wrapper.querySelector("p");

    if (firstP && firstP.textContent) {
      return firstP.textContent;
    }
  }
};

/**
 * Truncates the body of an article to a specified number of paragraphs.
 * It slices the body array up to the specified index, which defaults to 3.
 * If the body is empty or undefined, it returns an empty array.
 * @param body The body of the article as an array of HTML strings.
 * @param sliceIndex The index at which to truncate the body. Defaults to 3.
 * @returns An array containing the truncated body.
 */
export const getTruncatedBody = (
  body: string[],
  sliceIndex: number = 3
): string[] => {
  if (!body || body.length === 0) return [];

  const paragraphIndices = body
    .map((content, idx) => (content.startsWith("<p>") ? idx : null))
    .filter((idx) => idx !== null);

  const lastIndex =
    paragraphIndices.length > sliceIndex
      ? paragraphIndices[sliceIndex]
      : paragraphIndices[paragraphIndices.length - 1] || 3;

  const truncatedBody = body.slice(0, lastIndex + 1);

  return truncatedBody;
};

/**
 * Extracts the pathname from a URL path.
 */
export const getUrlPathFromAbsoluteUrl = (urlPath: string): string => {
  if (!urlPath) return "";

  const domains = [
    "https://uat.businesstimes.com.sg",
    "https://www.businesstimes.com.sg",
  ];

  if (domains.some((domain) => urlPath.includes(domain))) {
    const url = new URL(urlPath);

    return url.pathname; // Return only the pathname part of the URL
  }

  return urlPath;
};

/**
 *
 * @param shortenerUrls Array of ShortenerUrlModel objects.
 * @returns The URL for the "web" device, or undefined if not found.
 * If the input array is undefined or empty, it returns undefined.
 * If there is no entry for the "web" device, it returns undefined.
 * If an entry for the "web" device is found, it returns the corresponding URL.
 */
export function shortenerUrlForWeb(
  shortenerUrls: ShortenerUrlModel[] | undefined
): string | undefined {
  if (!shortenerUrls || shortenerUrls.length === 0) return undefined;

  const webShortener = shortenerUrls.find(
    (item) => item.device.toLowerCase() === "web"
  );

  return webShortener?.url;
}

/**
 * Checks if the article is a lifestyle story using the uniqueName of the lifestyle section.
 * @param sections The sections of the article.
 */
export const isLifestyleStory = (sections: FeedSection[]): boolean => {
  return sections.some(({ uniqueName }) =>
    ["lifestyle", "lifestyle_bt-luxe"].includes(uniqueName)
  );
};

/**
 * Checks if the article is a global story using the uniqueName of the global section.
 * @param sections The sections of the article.
 */
export const isGlobalStory = (sections: FeedSection[]): boolean => {
  return sections.some(({ uniqueName }) =>
    ["international_global"].includes(uniqueName)
  );
};

/**
 * Helper function to get the ad settings for the article
 * @param articleData - The article data
 * @returns
 */
export const getAdSettings = (articleData: FeedArticleItems) => {
  const section = articleData.sections[0];
  const mainSectionURLPath = getUrlPathFromAbsoluteUrl(section.urlPath);

  const disablePrestitialBasedOnUrlPath = [
    RouteFactory.paidPressRelease, // paid press release pages
    RouteFactory.section("events-awards"), // events-awards pages
  ].some((path) => articleData?.urlPath.startsWith(path));

  const disablePrestitialBasedOnTags = [RouteFactory.keywordsBudget2025].some(
    (keyword) => articleData.tags?.some((tag) => tag.urlPath === keyword)
  );

  const adsNames: AdUnitNames[] =
    disablePrestitialBasedOnUrlPath || disablePrestitialBasedOnTags
      ? ["lb1", "imu1", "imu2", "imu3", "midarticlespecial", "catfish"]
      : [
          "lb1",
          "imu1",
          "imu2",
          "imu3",
          "midarticlespecial",
          "prestitial",
          "catfish",
        ];

  const adsProps: AdSettingsProps = {
    adNames: adsNames,
    path: mainSectionURLPath || "/",
    pageType: "articles",
    adTargets: [{ key: "page", value: "article" }],
    slotTargets: [
      { key: "btarticleid", value: articleData.id },
      {
        key: "bttags",
        value:
          articleData.tags
            ?.map((tag: FeedTag) => getFormattedTextForAds(tag.name))
            .join(",") || "",
      },
      {
        key: "gs_channels",
        value: articleData.sentiment?.keywords || "",
      },
    ],
  };

  return adsProps;
};

/**
 * Helper function to get the GA data for the article
 * @param articleData - The article data
 * @param isChinese - Whether the article is in Chinese
 * @returns
 */
export const getGAData = (
  articleData: FeedArticleItems,
  isChinese: boolean
) => {
  const mainSection = articleData.sections?.[0];
  const storyThread = getArticleStoryThread(articleData.tags || []);
  const pubdate = dayjs(articleData.publishTime)
    .tz(dayjsSingaporeTimezone)
    .format("YYYY-MM-DD HH:mm:ss");

  // missing adsvariant key, userkeyword, product_flag
  const gaProps: GADataProps = {
    articleid: articleData.id,
    cue_articleid: articleData.id,
    title: replaceProblematicSymbols(articleData.title),
    author: getArticleAuthorNamesV2(articleData.authors),
    chapter1: mainSection.parent ? mainSection.name.toLowerCase() : "",
    chapter2: storyThread?.name.toLowerCase() || "",
    contentcat: articleData.paidMode === ArticlePaidModeType.PREMIUM ? 2 : 1,
    contenttype: "1",
    keyword: getArticleKeywords(articleData.tags || []),
    level2: (mainSection.parent?.name || mainSection.name).toLowerCase(),
    pubdate: pubdate,
    story_threads: storyThread?.name.toLowerCase() || "",
    gsChannels: articleData.sentiment?.keywords || "",
    language: isChinese ? "sc" : "en",
    cd_version: "bt2",
  };

  return gaProps;
};

/**
 * Helper function to get the meta tags for the article
 * @param articleData - The article data
 * @returns
 */
export const getMetaTags = (
  articleData: FeedArticleItems,
  isChinese: boolean = false
): MetaTagDescriptor[] => {
  const {
    seoFields,
    title,
    authors,
    id,
    standfirst,
    blurb,
    urlPath,
    media,
    publishTime,
    editedTime,
    sentiment,
    paidMode,
    sections,
    availableTranslations,
    copyright,
  } = articleData;

  const parentSection = sections?.[0].parent || sections?.[0];

  const envDomain = getEnvironmentDomain(getEnvironment());
  const originalRelativePath = urlPath.replace("/zh-hans", "");
  const isZhHansAvailable = availableTranslations?.includes("zh-hans");

  const metaTags = MetaTags({
    title: renderPageTitleV2({
      kind: "art",
      title: replaceProblematicSymbols(seoFields?.metaTitle || title),
    }),
    author: getArticleAuthorNamesV2(authors),
    articleId: id,
    description: standfirst || blurb || "",
    keywords: articleData.tags?.map((tag) => tag.name),
    slug: getUrlPathFromAbsoluteUrl(urlPath),
    canonicalUrl: seoFields?.canonicalUrl,
    imageUrl: media?.[0]?.original?.url,
    imageAlt: media?.[0]?.caption || title,
    jsonLd: getArticleJSONLD(articleData, isChinese ? "zh-Hans" : "en-SG"),
    robots: "index, follow, max-image-preview:large",
    gsChannels: sentiment?.keywords || "",
    articleCategory: parentSection?.name || "",
    accessFlag:
      paidMode === ArticlePaidModeType.PREMIUM
        ? AccessFlag.Premium
        : AccessFlag.Free,
    publishedDate: publishTime,
    copyright,
    updatedDate: editedTime,
    ogTitle: seoFields?.ogTitle || title,
    ogDescription: seoFields?.ogDescription,
    ogImageUrl: seoFields?.ogImages?.[0]?.original?.url,
    alternates: [
      {
        hrefLang: "x-default",
        href: `${envDomain}${originalRelativePath}`,
      },
      {
        hrefLang: "en",
        href: `${envDomain}${originalRelativePath}`,
      },
      ...(isZhHansAvailable
        ? [
            {
              hrefLang: "zh-Hans",
              href: `${envDomain}/zh-hans${originalRelativePath}`,
            },
          ]
        : []),
    ],
    ogLocale: isChinese ? "zh_Hans" : "en_SG",
  });

  return metaTags;
};

/**
 * Helper function to check if the article is a events awards story using the uniqueName of the events awards section.
 * @param sections The sections of the article.
 * @returns {boolean} True if the article is a events awards story, false otherwise.
 */
export const isEventsAwardsStory = (sections: FeedSection[]): boolean => {
  return sections.some(({ uniqueName }) =>
    [
      "events-awards",
      "events-awards_singapore-business-awards",
      "events-awards_singapore-corporate-awards",
      "events-awards_enterprise-50",
      "events-awards_emerging-enterprise",
      "events-awards_sustainability-impact-awards",
      "events-awards_design-ai-and-tech-awards",
    ].includes(uniqueName)
  );
};

/**
 * Get the cdp paywall module id based on the days ago
 * @returns {string} cdp paywall module id
 */
export const getCdpPaywallModuleId = ({
  isTruncated30DaysAgo,
  isTruncated60DaysAgo,
}: {
  isTruncated30DaysAgo: boolean;
  isTruncated60DaysAgo: boolean;
}): number => {
  if (isTruncated60DaysAgo) return CDP_PAYWALL_180_DAYS_MODULE_ID;

  if (isTruncated30DaysAgo) return CDP_PAYWALL_30_DAYS_MODULE_ID;

  return CDP_PAYWALL_MODULE_ID;
};
